import { useState, useEffect, useMemo } from 'react';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DatagridPremiumUi, GridCellExpand } from '../Interface';
import { DocStatusList } from '../../config/document';
import documentService from '../../service/newDoc.service';
import dayjs from 'dayjs';
import { Chip } from '@mui/material';
import { DocumentDirection } from '../../config/DocumentDirection';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import { formatCurrency, getRounding } from '../../utils/currencyHelper';
import useGenerateJokerReportCols from '../../hooks/useGenerateJokerReportCols';
import useConfig from '../../context/ConfigContext';

const DocumentReports = ({ docState }) => {
  const {
    setRefetchTable,
    refetchTable,
    viewField,
    setSelectedValues,
    operatorsDisabled,
    setRowSelectionModel,
    autocompleteOptions,
    autocompleteData,
    datagridIdentifiers,
    rowSelectionModel,
    docTypeId,
    documentData,
    jokerFields,
  } = docState;

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const { params } = useParam();
  const { config } = useConfig();

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'docNum', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getRowRounding = (row) => {
    const currency = autocompleteData.currency.find((c) => c.code === row.currencyCode);

    return getRounding(documentData.direction, currency);
  };

  const getOwnRowRounding = () => {
    const currency = autocompleteData.currency.find((c) => c.code === config.currencyCode);
    return getRounding(documentData.direction, currency);
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const getList = (gridState) => {
    documentService.getDocuments(gridState, docTypeId).then((data) => {
      setReports(data);
    });
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const renderCellExpand = (data) => {
    return <GridCellExpand value={data.value || ''} width={data.colDef.computedWidth} />;
  };

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'docNum',
        headerName: 'Bizonylatszám',
        type: 'string',
      },
      {
        field: 'docStatus',
        headerName: 'Státusz',
        type: 'singleSelect',
        sortable: false,
        valueOptions: DocStatusList,
      },
      {
        field: 'companyId',
        headerName: 'Cég',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.company?.code;
        },
        valueOptions: autocompleteOptions.company,
      },

      {
        field: 'companyName',
        headerName: 'Cég neve',
        type: 'string',
      },
      {
        field: 'docDate',
        headerName: 'Teljesítés dátuma',
        type: 'date',
        valueGetter: (_data, row) => {
          return new Date(row.docDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.docDate).format('YYYY-MM-DD');
        },
      },
      {
        field: 'issueDate',
        headerName: 'Kiállítás dátuma',
        type: 'date',
        valueGetter: (_data, row) => {
          return new Date(row.issueDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.issueDate).format('YYYY-MM-DD');
        },
      },

      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        valueGetter: (_data, row) => {
          return new Date(row.dueDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.dueDate).format('YYYY-MM-DD');
        },
      },
      {
        field: 'projectId',
        headerName: 'Projekt',
        type: 'string',
        sortable: false,
        renderCell: (data) => {
          return data.row.project?.projectName;
        },
      },
      {
        field: 'projectOrderNumber',
        headerName: getProjectOrderNumberName(params),
        type: 'string',
        sortable: false,
        renderCell: (data) => {
          return data.row.project?.orderNumber;
        },
      },
      {
        field: 'reference',
        headerName: 'Hivatkozási szám',
        type: 'string',
      },
      {
        field: 'costCenterCode',
        headerName: 'Költséghely',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.costCenter?.name;
        },
        valueOptions: autocompleteOptions.costCenter,
      },
      ...(documentData?.enableSettlement
        ? [
            {
              field: 'balanceSettled',
              headerName: 'Kintlévőség',
              type: 'singleSelect',
              renderCell: (data) => {
                if (data.row.balanceSettled)
                  return <Chip label="Kiegyenlített" color="success" size="small" variant="outlined" />;
                return (
                  <Chip
                    label={`${documentData.direction === DocumentDirection.INCOMING ? 'Követel' : 'Tartozik'}`}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                );
              },
              valueOptions: [
                { value: 'true', label: 'Kiegyenlített' },
                {
                  value: 'false',
                  label: documentData.direction === DocumentDirection.INCOMING ? 'Követel' : 'Tartozik',
                },
              ],
            },
            {
              field: 'settledAmount',
              headerName: 'Eddig fizetve',
              type: 'number',
              renderCell: (data) => {
                return formatCurrency(data.row.settledAmount);
              },
            },
            {
              field: 'openAmount',
              headerName: 'Nyitott összeg',
              type: 'number',
              renderCell: (data) => {
                return formatCurrency(data.row.openAmount, getRowRounding(data.row));
              },
            },
          ]
        : []),
      {
        field: 'currencyCode',
        headerName: 'Pénznem',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.currencyCode;
        },
        valueOptions: autocompleteOptions.currency,
      },
      {
        field: 'netInDocCur',
        headerName: 'Összesen nettó',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.netInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'VATInDocCur',
        headerName: 'Összesen Áfa',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.VATInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'sumInDocCur',
        headerName: 'Összesen bruttó',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.sumInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'netInOwnCur',
        headerName: 'Összesen nettó saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.netInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'VATInOwnCur',
        headerName: 'Összesen Áfa saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.VATInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'sumInOwnCur',
        headerName: 'Összesen bruttó saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.sumInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'note',
        headerName: 'Megjegyzés',
        type: 'string',
        renderCell: renderCellExpand,
      },
      ...jokerCols,
    ]);
  }, [autocompleteOptions, documentData, jokerCols]);

  return (
    <>
      {Object.keys(autocompleteOptions).length > 0 && Object.keys(documentData).length > 0 && (
        <DatagridPremiumUi
          key={datagridIdentifiers.doc}
          datagridIdentifier={datagridIdentifiers.doc}
          datagridInitialState={initialState}
          dataLoader={getList}
          rows={reports.rows}
          refetch={{ refetchTable, setRefetchTable }}
          rowCount={reports.rowCount}
          columns={[...columns]}
          onRowClick={(rowData) => {
            if (!viewField) return;
            setSelectedValues(rowData.row);
          }}
          paginationAndfilterMode="server"
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          disableRowSelectionOnClick={!viewField}
          getRowId={(row) => row.docId}
        />
      )}
    </>
  );
};

export default DocumentReports;
